import React, { useState, useEffect } from "react";
import axios from "../../../../utils/axios";
import { useSnackbar } from "notistack";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const EditPoPopup = ({ po, rfqId, onUpdateSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    supplierId: "",
    vendorName: "",
    poNumber: "",
    poAmount: "",
    createdByName: "",
    createdOn: "",
    remarks: "",
    grnDetails: [],
  });

  const [initialData, setInitialData] = useState(null);

  useEffect(() => {
    if (po) {
      const data = {
        supplierId: po.supplierId || "",
        vendorName: po.vendorName || "",
        poNumber: po.poNumber || "",
        poAmount: po.poAmount || "",
        createdByName: po.createdByName || "",
        createdOn: po.createdOn?.substring(0, 10) || "",
        remarks: po.remarks || "",
        grnDetails:
        po.grnDetails && po.grnDetails.length > 0
          ? po.grnDetails.map((grn) => ({
              _id: grn._id || "",
              grnNumber: grn.grnNumber || "",
              grnDate: grn.grnDate?.substring(0, 10) || "",
              grnComments: grn.grnComments || "",
            }))
          : [
              {
                _id: "",
                grnNumber: "",
                grnDate: "",
                grnComments: "",
              },
            ],
      };
      setFormData(data);
      setInitialData(data);
    }
  }, [po]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({ ...prev, remarks: value }));
  };

  const handleGrnChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      grnDetails: prev.grnDetails.map((grn, i) =>
        i === index ? { ...grn, [field]: value } : grn
      ),
    }));
  };

  const handleGrnQuillChange = (index, value) => {
    handleGrnChange(index, "grnComments", value);
  };

  const handleAddGrn = () => {
    setFormData((prev) => ({
      ...prev,
      grnDetails: [
        ...prev.grnDetails,
        {
          _id: "",
          grnNumber: "",
          grnDate: "",
          grnComments: "",
        },
      ],
    }));
  };

  const handleRemoveGrn = (index) => {
    if (window.confirm("Are you sure you want to remove this GRN?")) {
      setFormData((prev) => ({
        ...prev,
        grnDetails: prev.grnDetails.filter((_, i) => i !== index),
      }));
    }
  };

  const sanitizedGrns = formData.grnDetails.filter(
    (grn) =>
      grn.grnNumber?.trim() ||
      grn.grnDate ||
      grn.grnComments?.replace(/<(.|\n)*?>/g, "").trim()
  );    

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        rfqId,
        supplierId: formData.supplierId,
        poNumber: formData.poNumber,
        updatedPO: {
          poAmount: formData.poAmount,
          createdByName: formData.createdByName,
          createdOn: formData.createdOn,
          remarks: formData.remarks,
          grnDetails: sanitizedGrns.map((grn) => ({
            grnId: grn._id || undefined,
            grnNumber: grn.grnNumber,
            grnDate: grn.grnDate,
            grnComments: grn.grnComments,
          }))
        },
      };

      const response = await axios.put(
        "/api/rfqs/po/update",
        payload
      );

      if (response.data.error) {
        enqueueSnackbar(response.data.message || "Failed to update PO", {
          variant: "error",
        });
        return;
      }

      enqueueSnackbar("PO updated successfully", { variant: "success" });

      if (onUpdateSuccess) {
        onUpdateSuccess({
          ...po,
          ...payload.updatedPO,
          grnDetails: sanitizedGrns,
        });
      }
    } catch (error) {
      console.error("Error updating PO:", error);
      enqueueSnackbar("Server error while updating PO", { variant: "error" });
    }
  };

  // 🔍 Compare current form data with initial
  const isFormChanged = () => {
    if (!initialData) return false;

    // Check PO fields
    const poFieldsChanged =
      formData.poAmount !== initialData.poAmount ||
      formData.createdByName !== initialData.createdByName ||
      formData.createdOn !== initialData.createdOn ||
      formData.remarks !== initialData.remarks;

    // Check GRN details
    const grnDetailsChanged =
      JSON.stringify(formData.grnDetails) !==
      JSON.stringify(initialData.grnDetails);

    return poFieldsChanged || grnDetailsChanged;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 px-4 pt-4 text-sm">
      <div className="grid grid-cols-26 text-sm gap-2">
        <div className="col-span-4">
          <label className="block mb-1 font-medium">
            Vendor Name
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="vendorName"
            value={formData.vendorName}
            disabled
            className="w-full text-sm border-gray-300 border px-2 py-1"
          />
        </div>

        <div className="col-span-3">
          <label className="block mb-1 font-medium">
            PO Number
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="poNumber"
            value={formData.poNumber}
            disabled
            className="w-full text-sm border-gray-300 border px-2 py-1"
          />
        </div>

        <div className="col-span-3">
          <label className="block mb-1 font-medium">
            PO Amount
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="poAmount"
            value={formData.poAmount}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only numbers and up to 2 decimal places
              const regex = /^\d*\.?\d{0,2}$/;
              if (value === "" || regex.test(value)) {
                setFormData((prev) => ({ ...prev, poAmount: value }));
              }
            }}
            className="w-full text-sm border-gray-300 border px-2 py-1"
            required
          />
        </div>

        <div className="col-span-5">
          <label className="block mb-1 font-medium">
            Created By
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="createdByName"
            value={formData.createdByName}
            onChange={handleChange}
            className="w-full text-sm border-gray-300 border px-2 py-1"
            required
          />
        </div>

        <div className="col-span-3">
          <label className="block mb-1 font-medium">
            Created On
            <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            name="createdOn"
            value={formData.createdOn}
            onChange={handleChange}
            className="w-full text-sm border-gray-300 border px-2 py-1"
            required
          />
        </div>

        <div className="col-span-8">
          <label className="block mb-1 font-medium">Remarks</label>
          <ReactQuill
            value={formData.remarks}
            onChange={handleQuillChange}
            theme="snow"
            className="min-h-[30px] max-h-[60px] p-0 word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full overflow-y-auto remove-quill-padding"
            modules={{ toolbar: false }}
          />
        </div>
      </div>
      {/* GRN Details Section */}
        <div className="mt-4 mb-6">
        <div className="grid grid-cols-26 gap-1">
          <div className="col-span-1"></div>
          <p className="col-span-3 text-left text-sm font-medium text-gray-700">
            GRN Details
          </p>
        </div>

        <div className="bg-gray-500 bg-opacity-50 w-full">
          <div className="bg-white w-full">
            {/* GRN Table Header */}
            <div className="grid grid-cols-26 gap-1 pb-2 pt-3 text-sm text-[#777777]">
              <p className="col-span-1"></p>
              <p className="text-left col-span-1">Sr#</p>
              <p className="text-center col-span-4">GRN No.</p>
              <p className="text-center col-span-3">GRN Date</p>
              <p className="text-center col-span-7">GRN Comments</p>
              <p className="text-center col-span-2"></p>
            </div>

            {/* GRN Table Rows */}
            <div className="text-sm">
              {formData.grnDetails.map((grnItem, grnIndex) => (
                  <div key={grnIndex} className="grid grid-cols-26 gap-2 items-start mb-2">
                    <div className="col-span-1"></div>
                    <div className="text-left col-span-1">
                      {grnIndex + 1}
                    </div>
                    <div className="col-span-4">
                      <input
                        type="text"
                        value={grnItem.grnNumber || ""}
                        onChange={(e) =>
                          handleGrnChange(grnIndex, "grnNumber", e.target.value)
                        }
                        className="w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                        
                      />
                    </div>
                    <div className="col-span-3">
                      <input
                        type="date"
                        value={grnItem.grnDate || ""}
                        onChange={(e) =>
                          handleGrnChange(grnIndex, "grnDate", e.target.value)
                        }
                        className="w-full border border-gray-300 px-2 py-1 text-sm focus:ring-0"
                        
                      />
                    </div>
                    <div className="col-span-7">
                      <ReactQuill
                        value={grnItem.grnComments || ""}
                        onChange={(value) =>
                          handleGrnChange(grnIndex, "grnComments", value)
                        }
                        theme="snow"
                        className="min-h-[28px] max-h-[80px] p-0 word-break whitespace-normal focus:outline-none focus:ring-0 text-sm w-full overflow-y-auto remove-quill-padding"
                        modules={{ toolbar: false }}
                      />
                    </div>

                    <div className="col-span-1 flex items-center mx-2 mt-1 gap-x-2">
                      {/* Always show + button for all rows */}
                      <button
                        type="button"
                        className="w-fit text-lg"
                        onClick={() => {
                          const newGrnDetails = [...formData.grnDetails];
                          newGrnDetails.splice(grnIndex + 1, 0, {
                            _id: "",
                            grnNumber: "",
                            grnDate: "",
                            grnComments: ""
                          });
                          setFormData(prev => ({
                            ...prev,
                            grnDetails: newGrnDetails
                          }));
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m7-7H5" />
                        </svg>
                      </button>

                      {/* Show - button only if there is more than one row */}
                      {(formData.grnDetails.length > 1 || 
                        (formData.grnDetails.length === 0 && grnIndex > 0)) && (
                        <button
                          type="button"
                          onClick={() => {
                            if (formData.grnDetails.length === 0) {
                              // If showing default empty rows (not in array yet)
                              // Just clear the fields for this row
                              const newGrnDetails = [...formData.grnDetails];
                              newGrnDetails[grnIndex] = {
                                ...newGrnDetails[grnIndex],
                                grnNumber: "",
                                grnDate: "",
                                grnComments: ""
                              };
                              setFormData(prev => ({
                                ...prev,
                                grnDetails: newGrnDetails
                              }));
                            } else {
                              // Remove the row from the array
                              const newGrnDetails = formData.grnDetails.filter((_, i) => i !== grnIndex);
                              setFormData(prev => ({
                                ...prev,
                                grnDetails: newGrnDetails
                              }));
                            }
                          }}
                          className="text-lg"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end sticky bottom-0 py-4 border-t bg-white space-x-4">
        <button
          type="submit"
          disabled={!isFormChanged()}
          className={`px-4 py-1 text-white ${
            isFormChanged()
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Update PO
        </button>
      </div>
    </form>
  );
};

export default EditPoPopup;
